import React, {
	Fragment,
	useEffect,
	useMemo,
	useState,
} from 'react';
import { HiOutlinePencil, HiOutlineTrash } from 'react-icons/hi';
import { useDispatch, useSelector } from 'react-redux';
import { DataTable } from 'components/shared';
import { getEmailTemplateList, setTableData } from '../store/dataSlice';
import { setSortedColumn } from '../store/stateSlice';
import useThemeClass from 'utils/hooks/useThemeClass';
import { DoubleSidedImage } from 'components/shared';
import { useNavigate } from 'react-router-dom';
import isEmpty from 'lodash/isEmpty';
import cloneDeep from 'lodash/cloneDeep';
import { toggleDeleteConfirmation } from '../store/stateSlice';
import { setSelectedProduct } from '../store/stateSlice';
import EmailTemplateDeleteConfirmation from './EmailTemplateDeleteConfirmation';
import { useTranslation } from 'react-i18next';
import { Tooltip } from 'components/ui';

const ActionColumn = ({ row }) => {
	const { textTheme } = useThemeClass()
	const navigate = useNavigate()
	const dispatch = useDispatch()
	const { t } = useTranslation();
	const isMobileScreen = window.innerWidth <= 992;
	const onEdit = () => {
		navigate(`/cms/Email/EmailTemplate/Edit/${row.id}`)
	}

	const onDelete = () => {
		dispatch(toggleDeleteConfirmation(true))
		dispatch(setSelectedProduct(row.id))
	}

	return (
		<div className="flex flex-row justify-start text-lg">
			{isMobileScreen ? (
				<>
					<span className={`cursor-pointer p-2 hover:${textTheme}`} onClick={onEdit}>
						<HiOutlinePencil style={{ fontSize: '17px' }} />
					</span>

					<span className="cursor-pointer p-2 hover:text-red-500" onClick={onDelete}>
						<HiOutlineTrash style={{ fontSize: '17px' }} />
					</span>
				</>
			) : (
				<>
					<Tooltip title={t('translation.edit')} placement="bottom">
						<span className={`cursor-pointer p-2 hover:${textTheme}`} onClick={onEdit}>
							<HiOutlinePencil style={{ fontSize: '17px' }} />
						</span>
					</Tooltip>
					<Tooltip title={t('tooltip.Delete')} placement="bottom">
						<span className="cursor-pointer p-2 hover:text-red-500" onClick={onDelete}>
							<HiOutlineTrash style={{ fontSize: '17px' }} />
						</span>
					</Tooltip>
				</>
			)}
		</div>
	)
}

const EmailTemplateTable = () => {
	const dispatch = useDispatch();
	const { pageIndex, pageSize, sort, query, total } = useSelector((state) => state.emailTemplatesList.data.tableData);
	const webLangId = useSelector((state) => state.selectLang.data.selectedLanguage);
	const filterData = useSelector((state) => state.emailTemplatesList.data.filterData);
	//const loading = useSelector((state) => state.emailTemplatesList.data.loading);
	const data = useSelector((state) => state.emailTemplatesList.data.productList);
	const { t } = useTranslation();
	//const { locale } = useSelector((state) => state.theme);

	useEffect(() => {
		fetchData()
	}, [pageIndex, pageSize, sort, webLangId])

	const tableData = useMemo(() =>
		({ pageIndex, pageSize, sort, query, total, webLangId }),
		[pageIndex, pageSize, sort, query, total, webLangId])

	const [dataLoaded, setDataLoaded] = useState(false);

	const fetchData = () => {
		dispatch(getEmailTemplateList({ pageIndex, pageSize, sort, query, filterData, webLangId }))
			.then(() => {
				setDataLoaded(true);
			});
	};

	const columns = useMemo(() => [
		{
			Header: `${t('translation.nameLocaleTable')}`,
			accessor: `name`,
			sortable: true,
			Cell: props => {
				const row = props.row.original
				return (
					<span>{row.name}</span>
				);
			},
		},
		{
			Header: `${t('translation.subject')}`,
			accessor: 'subject',
			sortable: true,
			Cell: props => {
				const row = props.row.original
				return (
					<span>{row.subject}</span>
				)
			},
		},
		{
			Header: `${t('translation.content')}`,
			accessor: 'content',
			sortable: true,
			Cell: props => {
				const row = props.row.original
				const [showFullContent, setShowContent] = useState(false);

				function handleContent() {
					setShowContent(!showFullContent);
				}

				return (
					<span onClick={handleContent} dangerouslySetInnerHTML={{ __html: row.content } && (showFullContent ? { __html: row.content } : { __html: row.content.slice(0, 20) + "..." })}></span>
				)
			},
		},
		{
			Header: `${t('translation.action')}`,
			id: 'action',
			draggableId: 'action',
			droppable: true,
			accessor: (row) => row,
			Cell: props => <ActionColumn row={props.row.original} />
		},
	], [])

	const onPaginationChange = page => {
		const newTableData = cloneDeep(tableData)
		newTableData.pageIndex = page
		dispatch(setTableData(newTableData))
	}

	const onSelectChange = value => {
		const newTableData = cloneDeep(tableData)
		newTableData.pageSize = Number(value)
		newTableData.pageIndex = 1
		dispatch(setTableData(newTableData))
	}

	const onSort = (sort, sortingColumn) => {
		const newTableData = cloneDeep(tableData)
		newTableData.sort = sort
		dispatch(setTableData(newTableData))
		dispatch(setSortedColumn(sortingColumn))
	}

	return (
		<Fragment>
			<div className="card-border">
				<DataTable
					columns={columns}
					data={data}
					skeletonAvatarColumns={[0]}
					skeletonAvatarProps={{ className: 'rounded-md' }}
					loading={!dataLoaded}
					pagingData={tableData}
					onPaginationChange={onPaginationChange}
					onSelectChange={onSelectChange}
					onSort={onSort}
				/>
				{(dataLoaded && isEmpty(data)) && (
					<div className="h-full w-full py-4 flex flex-col items-center justify-center">
						<DoubleSidedImage
							src="/img/others/img-2.png"
							darkModeSrc="/img/others/img-2-dark.png"
							alt={t('translation.notFound')}
						/>
						<h3 className="mt-8">{t('translation.notFound')}</h3>
					</div>
				)}
			</div>
			<EmailTemplateDeleteConfirmation />
		</Fragment>
	)
}

export default EmailTemplateTable
